"use strict";

/**
 * @namespace
 */
gc.gis.style = gc.gis.style || {};

/**
 * @classdesc
 * Klasse zur Verwaltung einer Farbe im RGBA-Format.
 *
 * @class
 * @memberof gc.gis.style
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {Array.<number>|string} opt_color
 */
gc.gis.style.GCColor = function(opt_color)
{
    /** @type int */
    this.r = 0;
    /** @type int */
    this.g = 0;
    /** @type int */
    this.b = 0;
    /** @type float */
    this.a = 1.0;

    if (opt_color) {
        this.parse(opt_color);
    }
};

/**
 * Parst die übergebene Farbe und überführt diese in die interne Struktur.
 *
 * @public
 * @method
 * @param {Array.<number>|string} color
 * @return {gc.gis.style.GCColor}
 */
gc.gis.style.GCColor.prototype.parse = function(color)
{
    var tmp;

    if (Array.isArray(color)) {
        if (color.length > 0) {
            this.setRed(color[0]);
        }
        if (color.length > 1) {
            this.setGreen(color[1]);
        }
        if (color.length > 2) {
            this.setBlue(color[2]);
        }
        if (color.length > 3) {
            this.setAlpha(color[3]);
        }
    }
    else if (typeof color === 'string') {
        // 6-stelliger HEX-Farbwert
        if (tmp = /^#([\da-fA-F]{2})([\da-fA-F]{2})([\da-fA-F]{2})/.exec(color)) {
            this.r = parseInt(tmp[1], 16);
            this.g = parseInt(tmp[2], 16);
            this.b = parseInt(tmp[3], 16);
        }
        // 3-stelliger HEX-Farbwert
        else if (tmp = /^#([\da-fA-F])([\da-fA-F])([\da-fA-F])/.exec(color)) {
            this.r = parseInt(tmp[1] + tmp[1], 16);
            this.g = parseInt(tmp[2] + tmp[2], 16);
            this.b = parseInt(tmp[3] + tmp[3], 16);
        }
        // rgba-Schreibweise
        else if (tmp = /^rgba\(([\d]+),([\d]+),([\d]+),([\d]+|[\d]*.[\d]+)\)/.exec(color)) {
            this.setRed(+tmp[1]);
            this.setGreen(+tmp[2]);
            this.setBlue(+tmp[3]);
            this.setAlpha(+tmp[4]);
        }
        // rgb-Schreibweise
        else if (tmp = /^rgb\(([\d]+),([\d]+),([\d]+)\)/.exec(color)) {
            this.setRed(+tmp[1]);
            this.setGreen(+tmp[2]);
            this.setBlue(+tmp[3]);
        }
    }

    return this;
};

/**
 * Setzt den Rotanteil der Farbe.
 *
 * @public
 * @method
 * @param {int} red
 * @return {gc.gis.style.GCColor}
 */
gc.gis.style.GCColor.prototype.setRed = function(red)
{
    if (red < 0) {
        this.r = 0;
    }
    else if (red > 255) {
        this.r = red % 255;
    }
    else {
        this.r = red;
    }

    return this;
};

/**
 * Liefert den Rotanteil der Farbe.
 *
 * @public
 * @method
 * @return {int}
 */
gc.gis.style.GCColor.prototype.getRed = function()
{
    return this.r;
};

/**
 * Setzt den Grünanteil der Farbe.
 *
 * @public
 * @method
 * @param {int} green
 * @return {gc.gis.style.GCColor}
 */
gc.gis.style.GCColor.prototype.setGreen = function(green)
{
    if (green < 0) {
        this.g = 0;
    }
    else if (green > 255) {
        this.g = green % 255;
    }
    else {
        this.g = green;
    }

    return this;
};

/**
 * Liefert den Grünanteil der Farbe.
 *
 * @public
 * @method
 * @return {int}
 */
gc.gis.style.GCColor.prototype.getGreen = function()
{
    return this.g;
};

/**
 * Setzt den Blauanteil der Farbe.
 *
 * @public
 * @method
 * @param {int} blue
 * @return {gc.gis.style.GCColor}
 */
gc.gis.style.GCColor.prototype.setBlue = function(blue)
{
    if (blue < 0) {
        this.b = 0;
    }
    else if (blue > 255) {
        this.b = blue % 255;
    }
    else {
        this.b = blue;
    }

    return this;
};

/**
 * Liefert den Blauanteil der Farbe.
 *
 * @public
 * @method
 * @return {int}
 */
gc.gis.style.GCColor.prototype.getBlue = function()
{
    return this.b;
};

/**
 * Setzt den Alpha-Anteil der Farbe.
 *
 * @public
 * @method
 * @param {float} alpha
 * @return {gc.gis.style.GCColor}
 */
gc.gis.style.GCColor.prototype.setAlpha = function(alpha)
{
    if (alpha < 0.0) {
        this.a = 0.0;
    }
    else if (alpha > 1.0) {
        this.a = 1.0;
    }
    else {
        this.a = alpha;
    }

    return this;
};

/**
 * Liefert den Alpha-Anteil der Farbe.
 *
 * @public
 * @method
 * @return {float}
 */
gc.gis.style.GCColor.prototype.getAlpha = function()
{
    return this.a;
};

/**
 * Liefert den Farbe in Form eines Arrays.
 *
 * @public
 * @method
 * @return {Array.<number>} Farbe in Array-Repräsentation [red, green, blue, alpha]
 */
gc.gis.style.GCColor.prototype.asArray = function()
{
    return [this.r, this.g, this.b, this.a];
};

/**
 * Gibt den Farbcode in der rgba()-Notation zurück.
 *
 * @public
 * @method
 * @return {string}
 */
gc.gis.style.GCColor.prototype.toString = function()
{
	return 'rgba(' + this.r + ',' + this.g + ',' + this.b + ',' + this.a + ')';
};

/**
 * Klont das Farbobjekt.
 *
 * @public
 * @method
 * @return {gc.gis.style.GCColor} Clone
 */
gc.gis.style.GCColor.prototype.clone = function()
{
	return new gc.gis.style.GCColor(this.asArray());
};
